import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  Icon,
  SimpleGrid,
} from '@chakra-ui/react';
import { PaperPlaneTilt, Buildings, ArrowRight } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import SecaoClienteLayout from '../layout';
import { formatCNPJ } from '../../../utils/FormatFunctions';

interface IEmpresa {
  id: number;
  external_id: string;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string | null;
}

interface ClienteData {
  id: number;
  guid: string;
  nome: string;
  email: string;
  empresas?: IEmpresa[];
}

const SecaoClienteDocumentos = () => {
  const navigate = useNavigate();

  // Pegar dados do cliente do localStorage
  const clienteDataStr = localStorage.getItem('cliente_data');
  const clienteData: ClienteData | null = clienteDataStr ? JSON.parse(clienteDataStr) : null;

  const empresasValidadas = useMemo(() => {
    return clienteData?.empresas || [];
  }, [clienteData?.empresas]);

  // Navegar para envio de documentos da empresa
  const handleEmpresaClick = (empresa: IEmpresa) => {
    navigate(`/secao-cliente/documentos/${empresa.external_id}`);
  };

  return (
    <SecaoClienteLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <HStack spacing={3}>
            <Box
              p={3}
              borderRadius="xl"
              bgGradient="linear(to-br, green.400, green.600)"
              boxShadow="lg"
            >
              <Icon as={PaperPlaneTilt} boxSize={6} color="white" weight="duotone" />
            </Box>
            <Box>
              <Heading size="lg" color="gray.800">
                Envio de Documentos
              </Heading>
              <Text color="gray.500" fontSize="sm">
                Envie documentos para o escritório de contabilidade
              </Text>
            </Box>
          </HStack>
        </Box>

        {/* Lista de empresas */}
        <Box>
          <HStack mb={4}>
            <Icon as={Buildings} boxSize={5} color="gray.500" />
            <Text fontWeight="600" color="gray.700">
              Selecione uma empresa
            </Text>
          </HStack>
          {empresasValidadas.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {empresasValidadas.map(empresa => (
                <Card
                  key={empresa.id}
                  variant="outline"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: 'green.300',
                    shadow: 'md',
                    transform: 'translateY(-2px)',
                  }}
                  onClick={() => handleEmpresaClick(empresa)}
                >
                  <CardBody>
                    <HStack spacing={3} justify="space-between">
                      <HStack spacing={3} flex={1} minW={0}>
                        <Box p={2} borderRadius="lg" bg="green.50">
                          <Icon as={Buildings} boxSize={5} color="green.500" />
                        </Box>
                        <Box flex={1} minW={0}>
                          <Text fontWeight="600" color="gray.800" noOfLines={1}>
                            {empresa.razao_social}
                          </Text>
                          {empresa.cnpj && (
                            <Text fontSize="sm" color="gray.500">
                              {formatCNPJ(empresa.cnpj)}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                      <Icon as={ArrowRight} boxSize={5} color="gray.400" />
                    </HStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          ) : (
            <Card variant="outline" bg="gray.50">
              <CardBody>
                <VStack py={8} spacing={4}>
                  <Icon as={Buildings} boxSize={12} color="gray.300" />
                  <Text color="gray.500" textAlign="center">
                    Nenhuma empresa vinculada encontrada
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          )}
        </Box>
      </VStack>
    </SecaoClienteLayout>
  );
};

export default SecaoClienteDocumentos;
